import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Truck, FileText, Trash2, Plus, Minus, ExternalLink, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal de Terceirização de Lote — permite criar, ver e cancelar terceirizações.
 * Cada terceirização ativa subtrai a quantidade do disponível do lote.
 */
export default function TerceirizacaoLoteModal({ open, onOpenChange, ofId, lote, operacoes = [], onSuccess }) {
  const [qtd, setQtd] = useState(1);
  const [fornecedor, setFornecedor] = useState('');
  const [operacaoId, setOperacaoId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const qtdLote = lote?.quantidade_ajustada || lote?.quantidade || 0;
  const qtdTerceirizada = lote?.quantidade_terceirizada || 0;
  const qtdDisponivel = Math.max(0, qtdLote - qtdTerceirizada);
  const terceirizacoes = lote?.terceirizacoes_ativas || lote?.terceirizacoes || [];
  const ativas = terceirizacoes.filter(t => t.status !== 'cancelada');

  useEffect(() => {
    if (open) {
      setQtd(Math.min(1, qtdDisponivel));
      setFornecedor('');
      setOperacaoId('');
      setObservacoes('');
    }
  }, [open, qtdDisponivel]);

  if (!lote) return null;

  const handleCriar = async () => {
    if (qtd <= 0 || qtd > qtdDisponivel) {
      toast.error('Quantidade inválida');
      return;
    }
    setSubmitting(true);
    try {
      const op = operacoes.find(o => o.operacao_id === operacaoId);
      const res = await axios.post(
        `${API}/ofs/${ofId}/lotes/${lote.id}/terceirizar`,
        {
          quantidade: qtd,
          fornecedor: fornecedor || null,
          operacao_id: operacaoId || null,
          operacao_nome: op?.operacao_nome || op?.nome || '',
          observacoes: observacoes || null,
        }
      );
      toast.success(`${res.data.terceirizacao.codigo} criada`);
      // Abrir o PDF automaticamente
      window.open(`${API}/ofs/${ofId}/lotes/${lote.id}/terceirizar/${res.data.terceirizacao.id}/pdf`, '_blank');
      if (onSuccess) onSuccess();
      // Reset campos para criar outra
      setQtd(1);
      setFornecedor('');
      setObservacoes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao terceirizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelar = async (tercId) => {
    if (!window.confirm('Cancelar esta terceirização? A quantidade volta a estar disponível.')) return;
    try {
      await axios.delete(`${API}/ofs/${ofId}/lotes/${lote.id}/terceirizar/${tercId}`);
      toast.success('Terceirização cancelada');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="terceirizacao-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-red-600" />
            Resgate para Terceirização
          </DialogTitle>
          <DialogDescription>
            Lote <strong>{lote.codigo_lote || lote.numero_lote}</strong> · {qtdLote} peças totais · <strong className="text-emerald-700">{qtdDisponivel} disponíveis</strong>
            {qtdTerceirizada > 0 && <span className="text-red-700"> · {qtdTerceirizada} em terceirização</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Formulário de criação */}
        <div className="rounded-lg border-2 border-red-200 bg-red-50/30 p-4 space-y-3">
          <h4 className="font-semibold text-red-700 text-sm flex items-center gap-1">
            <Plus className="h-4 w-4" /> Nova Terceirização
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Quantidade */}
            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Quantidade *</label>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" onClick={() => setQtd(Math.max(1, qtd - 1))} disabled={qtd <= 1} className="h-10 w-10" data-testid="terc-qtd-minus">
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  min={1}
                  max={qtdDisponivel}
                  value={qtd}
                  onChange={(e) => setQtd(Math.max(1, Math.min(qtdDisponivel, parseInt(e.target.value) || 1)))}
                  className="h-10 text-center text-lg font-bold"
                  data-testid="terc-qtd-input"
                />
                <Button size="icon" variant="outline" onClick={() => setQtd(Math.min(qtdDisponivel, qtd + 1))} disabled={qtd >= qtdDisponivel} className="h-10 w-10" data-testid="terc-qtd-plus">
                  <Plus className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setQtd(qtdDisponivel)} disabled={qtd === qtdDisponivel} className="h-10 text-xs whitespace-nowrap" data-testid="terc-qtd-max">
                  Máx
                </Button>
              </div>
              <p className="text-[11px] text-stone-500 mt-0.5">Máximo: {qtdDisponivel}</p>
            </div>

            {/* Fornecedor */}
            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Fornecedor</label>
              <Input
                value={fornecedor}
                onChange={(e) => setFornecedor(e.target.value)}
                placeholder="Nome do terceiro"
                className="h-10"
                data-testid="terc-fornecedor"
              />
            </div>
          </div>

          {operacoes.length > 0 && (
            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Operação (opcional)</label>
              <select
                value={operacaoId}
                onChange={(e) => setOperacaoId(e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white"
                data-testid="terc-operacao"
              >
                <option value="">— sem operação específica —</option>
                {operacoes.map(op => (
                  <option key={op.operacao_id || op.id} value={op.operacao_id || op.id}>
                    {op.operacao_codigo || op.codigo} {op.operacao_nome || op.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-stone-700 mb-1 block">Observações</label>
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Detalhes sobre a saída..."
              rows={2}
              className="text-sm"
              data-testid="terc-obs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={handleCriar} disabled={submitting || qtd <= 0 || qtd > qtdDisponivel} className="bg-red-600 hover:bg-red-700" data-testid="terc-criar-btn">
              <FileText className="h-4 w-4 mr-1" />
              {submitting ? 'A criar...' : 'Criar e Gerar Guia'}
            </Button>
          </div>
        </div>

        {/* Lista de terceirizações ativas */}
        {ativas.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-stone-700 text-sm">Terceirizações Activas ({ativas.length})</h4>
            {ativas.map(t => {
              let dataStr = '-';
              try {
                dataStr = new Date(t.criado_em).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
              } catch { /* ignore */ }
              return (
                <div key={t.id} className="flex items-center justify-between p-3 border border-stone-200 rounded-lg bg-white" data-testid={`terc-row-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-0.5 rounded font-mono text-xs font-bold bg-red-100 text-red-700">
                      {t.codigo}
                    </span>
                    <div>
                      <div className="text-sm font-semibold">{t.quantidade} peças</div>
                      <div className="text-xs text-stone-500">
                        {t.fornecedor || 'Sem fornecedor'} · {dataStr}
                        {t.operacao_nome && <span> · {t.operacao_nome}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      href={`${API}/ofs/${ofId}/lotes/${lote.id}/terceirizar/${t.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 rounded border border-stone-200 hover:bg-stone-50 text-xs"
                      data-testid={`terc-pdf-${t.id}`}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" /> Guia
                    </a>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCancelar(t.id)}
                      className="text-red-600 hover:text-red-800 h-8 px-2"
                      data-testid={`terc-cancelar-${t.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-3 border-t border-stone-200">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-1" /> Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
